import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { QueryRefetchInterval } from '@cast/shared-types';
import { ApaAgent } from '@cast/types-apa';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const APA_AGENTS_QUERY_KEY = 'apa-agents';

export const getApaAgentsQueryKey = (clusterId?: string) =>
  clusterId !== undefined
    ? ([APA_AGENTS_QUERY_KEY, clusterId] as const)
    : ([APA_AGENTS_QUERY_KEY] as const);

const getApaAgentsQueryOptions = ({
  organizationId,
  clusterId,
  enabled = true,
  refetchInterval = false,
  queryClient,
}: {
  organizationId?: string;
  clusterId?: string;
  enabled?: boolean;
  refetchInterval?: QueryRefetchInterval<ApaAgent[]>;
  queryClient: QueryClient;
}) =>
  queryOptions({
    queryKey: getApaAgentsQueryKey(clusterId),
    queryFn: async () => {
      const { data } = await apiClient.apa.getAgents({
        organizationId: organizationId!,
        clusterId,
      });

      // If clusterId is provided, update the query data with the new agent data
      if (clusterId) {
        queryClient.setQueriesData(
          {
            queryKey: getApaAgentsQueryKey(),
          },
          (agents: ApaAgent[]): ApaAgent[] => {
            return agents?.map((agent) => {
              if (agent.clusterId === clusterId) {
                return data.agents[0];
              }
              return agent;
            });
          }
        );
      }

      return data.agents;
    },
    enabled: enabled && !!organizationId,
    refetchInterval,
  });

export const useApaAgentsQuery = ({
  clusterId,
  refetchInterval = false,
  enabled = true,
}: {
  clusterId?: string;
  refetchInterval?: QueryRefetchInterval<ApaAgent[]>;
  enabled?: boolean;
} = {}) => {
  const { currentOrganization } = useOrganizations();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery(
    getApaAgentsQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      enabled,
      refetchInterval,
      queryClient,
    })
  );

  return { apaAgents: data, isLoading, error, refetch };
};
